import { ComponentType, ReactNode } from 'react';

import { DeserializedAppData } from '@api';

import MultiColorDiscord from '@uikit/icons/MultiColorDiscord';
import MultiColorLinkedin from '@uikit/icons/MultiColorLinkedin';
import MultiColorMedium from '@uikit/icons/MultiColorMedium';
import MultiColorReddit from '@uikit/icons/MultiColorReddit';
import MultiColorTelegram from '@uikit/icons/MultiColorTelegram';
import MultiColorYoutube from '@uikit/icons/MultiColorYoutube';
import Nft from '@uikit/icons/Nft';
import Star from '@uikit/icons/Star';
import Wallet2 from '@uikit/icons/Wallet2';
import XNeutral from '@uikit/icons/XNeutral';

import { APP_STATUS, getAppStatus } from '@shared/helpers/app';

import I18nInfoTooltip from '@store:web/components/common/I18nInfoTooltip/I18nInfoTooltip';
import TrustScoreTag from '@store:web/components/common/TrustScoreTag/TrustScoreTag';

interface AppDetailsStatListProps {
  app: DeserializedAppData;
}

const socialNameToIconName: Record<string, ComponentType> = {
  discord: MultiColorDiscord,
  reddit: MultiColorReddit,
  telegram: MultiColorTelegram,
  twitter: () => <XNeutral className="text-neutral-700" />,
  medium: MultiColorMedium,
  youtube: MultiColorYoutube,
  linkedin: MultiColorLinkedin,
};
const socialNames = Object.keys(socialNameToIconName).map((name) => name.toLowerCase());

const AppDetailsStatList = ({ app }: AppDetailsStatListProps) => {
  const {
    attributes: {
      category,
      chainName,
      chainRank,
      rating,
      validatorsScore,
      generalRanking,
      categoryRank,
      isVerified,
      isHaveToken,
      isHaveNft,
      trustScore,
      productionReady,
      status,
    },
    socials,
  } = app;
  const appStatus = getAppStatus({
    productionReady,
    status,
  });
  const scores = [
    {
      label: (
        <>
          User Score&nbsp;
          <I18nInfoTooltip
            title="appPage_userScore_title"
            content="appPage_userScore_description"
          />
        </>
      ),
      content: (
        <>
          <Star className="icon-xs text-secondary" />
          {Number(rating) || ''}
        </>
      ),
    },
    {
      label: (
        <>
          Validation Score&nbsp;
          <I18nInfoTooltip
            title="appPage_validationScore_title"
            content="appPage_validationScore_description"
          />
        </>
      ),
      content: (
        <>
          <Star className="icon-xs text-secondary" />
          {Number(validatorsScore) || ''}
        </>
      ),
    },
    {
      label: <>Trust Score</>,
      content: <TrustScoreTag className="text-neutral-700" score={trustScore} />,
    },
  ];
  const ranks = [
    {
      label: (
        <>
          General&nbsp;
          <I18nInfoTooltip
            title="appPage_generalRanking_title"
            content="appPage_generalRanking_description"
          />
        </>
      ),
      content: `No. ${generalRanking}`,
      show: isVerified && !!generalRanking,
    },
    {
      label: category,
      content: `No. ${categoryRank}`,
      show: !!categoryRank,
    },
    {
      label: `${chainName}`,
      content: `No. ${chainRank}`,
      show: !!chainName,
    },
  ].filter((rank) => rank.show);
  const features: {
    show: boolean;
    label: ReactNode;
    Icon: ComponentType;
  }[] = [
    {
      label: 'Token',
      Icon: Wallet2,
      show: isHaveToken,
    },
    {
      label: 'NFT',
      Icon: Nft,
      show: isHaveNft,
    },
  ].filter((rank) => rank.show) as {
    show: boolean;
    label: ReactNode;
    Icon: ComponentType;
  }[];

  const socialList = socials
    ?.map((social) => {
      return {
        name: social?.attributes?.linkSite,
        url: social?.attributes?.link,
      };
    })
    .filter((social) => socialNames.includes(social.name.toLowerCase()));

  return (
    <div className="flex max-sm:-mx-store-container-px [&>*]:shrink-0 [&>*]:px-store-container-px sm:[&>*]:px-app-page-section-card-p overflow-x-auto divide-x divide-primary-200 h-24">
      {appStatus !== APP_STATUS.UPCOMING && (
        <>
          <ul className="flex gap-4 items-center">
            {scores.map((score, i) => {
              return (
                <li
                  key={i}
                  className="flex flex-col t-body4 justify-center items-center h-16 gap-1 px-4 border border-primary-200 rounded-md shadow-card"
                >
                  <span className="text-neutral-500" data-qa="info-score-item">
                    {score.label}
                  </span>
                  <span
                    className="font-semibold inline-flex items-center min-h-[1.25rem]"
                    data-qa="info-score-value"
                  >
                    {score.content}
                  </span>
                </li>
              );
            })}
          </ul>

          {ranks.length > 0 && (
            <ul className="flex gap-10 items-center">
              {ranks.map((rank, i) => {
                return (
                  <li key={i} className="flex flex-col t-body4 justify-center items-center gap-1.5">
                    <span className="text-neutral-500" data-qa="chart-title">
                      {rank.label}
                    </span>
                    <span className="font-semibold inline-flex" data-qa="chart-value">
                      {rank.content}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          {features.length > 0 && (
            <ul className="flex gap-10 items-center">
              {features.map(({ Icon, label }, i) => {
                return (
                  <li
                    key={i}
                    className="flex t-body4 items-center gap-4 font-semibold"
                    data-qa="info-token-tag"
                  >
                    <span
                      className="flex justify-center items-center w-12 h-12 flex-shrink-0 border rounded-md  text-primary border-primary-200 shadow-card"
                      data-qa="info-token-tag-icon"
                    >
                      <span className="icon-xs">
                        <Icon />
                      </span>
                    </span>
                    {label}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {!!socialList?.length && (
        <ul className="flex gap-3 items-center flex-grow justify-end">
          {socialList.map((social) => {
            const Icon = socialNameToIconName?.[social?.name?.toLocaleLowerCase()] || '';

            return (
              <li key={social.name}>
                <a
                  rel="noreferrer"
                  target="_blank"
                  href={social.url}
                  aria-label={social.name}
                  className="flex justify-center items-center w-12 h-12 flex-shrink-0 border rounded-md  text-primary border-primary-200 shadow-card transition-colors hover:border-primary-400"
                >
                  <span className="icon-lg">
                    <Icon />
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AppDetailsStatList;
